import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Heart, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

const PAYPAL_DONATION_URL = 'https://www.paypal.com/pools/c/9mux1BTDIv';

export const DonationButton = ({ variant = 'floating' }) => {
  const { language } = useLanguage();

  const handleDonate = () => {
    window.open(PAYPAL_DONATION_URL, '_blank', 'noopener,noreferrer');
  };

  if (variant === 'floating') {
    return (
      <button
        onClick={handleDonate}
        data-testid="donation-button-floating"
        className="fixed bottom-20 right-6 z-50 flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-gold to-gold-dark text-primary-foreground font-medium rounded-full shadow-glow hover:shadow-elevated hover:-translate-y-1 transition-all duration-300 group"
        title={language === 'fr' ? 'Faire un don' : 'Make a donation'}
      >
        <Heart className="w-5 h-5 fill-current group-hover:scale-110 transition-transform" />
        <span className="hidden sm:inline">
          {language === 'fr' ? 'Soutenir le projet' : 'Support the project'}
        </span>
      </button>
    );
  }

  if (variant === 'banner') {
    return (
      <div className="bg-gradient-to-r from-gold/10 via-gold/5 to-gold/10 border-y border-gold/20 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Heart className="w-5 h-5 text-accent fill-accent" />
            <h3 className="font-heading text-xl font-semibold text-foreground">
              {language === 'fr' ? 'Soutenez ce projet' : 'Support this project'}
            </h3>
            <Heart className="w-5 h-5 text-accent fill-accent" />
          </div>
          <p className="text-muted-foreground mb-4 max-w-xl mx-auto">
            {language === 'fr' 
              ? 'Ce site est gratuit et le restera. Si vous appréciez ce service, vous pouvez nous aider à le maintenir et à l\'améliorer.'
              : 'This site is free and will remain so. If you appreciate this service, you can help us maintain and improve it.'}
          </p>
          <Button 
            variant="sacred" 
            size="lg" 
            onClick={handleDonate}
            className="gap-2"
          >
            <Heart className="w-5 h-5" />
            {language === 'fr' ? 'Faire un don via PayPal' : 'Donate via PayPal'}
            <ExternalLink className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Default inline button
  return (
    <Button 
      variant="sacred" 
      onClick={handleDonate}
      className="gap-2"
    >
      <Heart className="w-4 h-4" />
      {language === 'fr' ? 'Faire un don' : 'Make a donation'}
    </Button>
  );
};

export const DonationSection = () => {
  const { language } = useLanguage();

  const handleDonate = () => {
    window.open(PAYPAL_DONATION_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="py-12 bg-gradient-to-b from-secondary/50 to-background">
      <div className="max-w-4xl mx-auto px-4 text-center">
        {/* Decorative cross */}
        <div className="flex justify-center mb-6">
          <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center">
            <Heart className="w-6 h-6 text-gold fill-gold/30" />
          </div>
        </div>

        <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground mb-4">
          {language === 'fr' ? 'Soutenez Mon Saint Patron' : 'Support My Patron Saint'}
        </h2>

        <p className="text-muted-foreground mb-6 max-w-2xl mx-auto leading-relaxed">
          {language === 'fr' 
            ? 'Ce site est entièrement gratuit et sans publicité. Votre générosité nous aide à maintenir ce service spirituel accessible à tous et à continuer d\'améliorer l\'expérience.'
            : 'This site is completely free and ad-free. Your generosity helps us keep this spiritual service accessible to everyone and continue improving the experience.'}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={handleDonate}
            className="group relative inline-flex items-center gap-3 px-8 py-4 bg-[#0070ba] hover:bg-[#005ea6] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
          >
            {/* PayPal logo */}
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
            </svg>
            <span>{language === 'fr' ? 'Faire un don avec PayPal' : 'Donate with PayPal'}</span>
            <ExternalLink className="w-4 h-4 opacity-70 group-hover:opacity-100" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground mt-6">
          {language === 'fr' 
            ? '🙏 Merci pour votre soutien et que Dieu vous bénisse !'
            : '🙏 Thank you for your support and God bless you!'}
        </p>
      </div>
    </section>
  );
};

export default DonationButton;
